import React, { useState, useEffect } from 'react';

// Mirrors the admin product search facets: category, location, supplier tier,
// unit price bucket, max MOQ and a photos-only refinement. Price and MOQ are
// single-select (matching the admin facet links); the rest are multi-select.

export const TIER_OPTIONS = [
  { value: 'top_verified', label: 'Nearshore Vetted' },
  { value: 'verified', label: 'Verified Factory' },
  { value: 'active', label: 'Active Factory' },
  { value: 'unclaimed', label: 'Unclaimed' },
];

// Values are "min-max" bucket strings the backend parses into price_min/price_max.
export const PRICE_OPTIONS = [
  { value: '-10', label: 'Under $10' },
  { value: '10-50', label: '$10 – $50' },
  { value: '50-250', label: '$50 – $250' },
  { value: '250-1000', label: '$250 – $1k' },
  { value: '1000-', label: '$1k+' },
];

export const MOQ_OPTIONS = [
  { value: '100', label: 'Up to 100 units' },
  { value: '500', label: 'Up to 500 units' },
  { value: '1000', label: 'Up to 1,000 units' },
  { value: '5000', label: 'Up to 5,000 units' },
];

function splitValues(value) {
  return value ? value.split(',').filter((v) => v) : [];
}

function ProductFilters({ filters, filterOptions, onSubmit }) {
  const [selected, setSelected] = useState({
    categories: splitValues(filters.categories),
    country: splitValues(filters.country),
    tier: splitValues(filters.tier),
    price: filters.price || '',
    moq_max: filters.moq_max || '',
    has_image: filters.has_image === 'true',
  });

  // Keep checkboxes honest when a filter is removed elsewhere (pills, clear all).
  useEffect(() => {
    setSelected({
      categories: splitValues(filters.categories),
      country: splitValues(filters.country),
      tier: splitValues(filters.tier),
      price: filters.price || '',
      moq_max: filters.moq_max || '',
      has_image: filters.has_image === 'true',
    });
  }, [filters]);

  const [expandedSections, setExpandedSections] = useState({
    categories: !!filters.categories,
    countries: !!filters.country,
    tier: !!filters.tier,
    price: !!filters.price,
    moq: !!filters.moq_max,
    refine: filters.has_image === 'true',
  });

  function toggleSection(section) {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  function submit(next) {
    onSubmit({
      categories: next.categories.join(','),
      country: next.country.join(','),
      tier: next.tier.join(','),
      price: next.price,
      moq_max: next.moq_max,
      has_image: next.has_image ? 'true' : '',
    });
  }

  function handleMultiToggle(filterType, value) {
    setSelected((prev) => {
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      const next = { ...prev, [filterType]: newValues };
      setTimeout(() => submit(next), 0);
      return next;
    });
  }

  // Single-select facets: clicking the active value clears it.
  function handleSingleToggle(filterType, value) {
    setSelected((prev) => {
      const next = { ...prev, [filterType]: prev[filterType] === value ? '' : value };
      setTimeout(() => submit(next), 0);
      return next;
    });
  }

  function handlePhotosToggle() {
    setSelected((prev) => {
      const next = { ...prev, has_image: !prev.has_image };
      setTimeout(() => submit(next), 0);
      return next;
    });
  }

  function handleRegionSelectAll(countries) {
    const countryCodes = countries.map((country) => country.code);
    const allSelected = countryCodes.every((code) => selected.country.includes(code));

    setSelected((prev) => {
      const newValues = allSelected
        ? prev.country.filter((code) => !countryCodes.includes(code))
        : [...new Set([...prev.country, ...countryCodes])];
      const next = { ...prev, country: newValues };
      setTimeout(() => submit(next), 0);
      return next;
    });
  }

  function renderSection(key, label, content) {
    return (
      <div className="filter-section mb-4">
        <div className="filter-header is-clickable" onClick={() => toggleSection(key)}>
          <span className="label mb-2">{label}</span>
          <span className="icon">
            <i className={`ph-bold ph-caret-${expandedSections[key] ? 'up' : 'down'}`}></i>
          </span>
        </div>
        <div className={`filter-content ${expandedSections[key] && 'is-expanded'}`}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="is-flex is-flex-direction-column">
      {renderSection(
        'categories',
        'Categories',
        filterOptions.categories
          ?.slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((category) => (
            <label key={category.name} className="checkbox mb-2">
              <input
                type="checkbox"
                checked={selected.categories.includes(category.name)}
                onChange={() => handleMultiToggle('categories', category.name)}
                className="mr-2"
              />
              {category.name}
            </label>
          ))
      )}

      {renderSection(
        'countries',
        'Location',
        Object.entries(filterOptions.countries || {}).map(([region, countries]) => {
          const countryCodes = countries.map((country) => country.code);
          const allSelected = countryCodes.every((code) => selected.country.includes(code));

          return (
            <div key={region} className="mb-3">
              <p className="has-text-weight-semibold mb-1">
                {region === 'Central America' ? 'LatAm' : region}
              </p>
              <a
                href="#"
                className="is-size-7 has-text-grey is-underlined mb-3 is-block"
                onClick={(e) => {
                  e.preventDefault();
                  handleRegionSelectAll(countries);
                }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </a>
              {countries
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((country) => (
                  <label key={country.code} className="checkbox mb-2">
                    <input
                      type="checkbox"
                      checked={selected.country.includes(country.code)}
                      onChange={() => handleMultiToggle('country', country.code)}
                      className="mr-2"
                    />
                    {country.name}
                  </label>
                ))}
            </div>
          );
        })
      )}

      {renderSection(
        'tier',
        'Supplier Tier',
        TIER_OPTIONS.map((tier) => (
          <label key={tier.value} className="checkbox mb-2">
            <input
              type="checkbox"
              checked={selected.tier.includes(tier.value)}
              onChange={() => handleMultiToggle('tier', tier.value)}
              className="mr-2"
            />
            {tier.label}
          </label>
        ))
      )}

      {renderSection(
        'price',
        'Unit Price',
        PRICE_OPTIONS.map((price) => (
          <label key={price.value} className="checkbox mb-2">
            <input
              type="checkbox"
              checked={selected.price === price.value}
              onChange={() => handleSingleToggle('price', price.value)}
              className="mr-2"
            />
            {price.label}
          </label>
        ))
      )}

      {renderSection(
        'moq',
        'Minimum Order',
        MOQ_OPTIONS.map((moq) => (
          <label key={moq.value} className="checkbox mb-2">
            <input
              type="checkbox"
              checked={selected.moq_max === moq.value}
              onChange={() => handleSingleToggle('moq_max', moq.value)}
              className="mr-2"
            />
            {moq.label}
          </label>
        ))
      )}

      {renderSection(
        'refine',
        'Refine',
        <label className="checkbox mb-2">
          <input
            type="checkbox"
            checked={selected.has_image}
            onChange={handlePhotosToggle}
            className="mr-2"
          />
          With photos only
        </label>
      )}
    </div>
  );
}

export default ProductFilters;
